import React, { useMemo } from 'react';
import { MessageSquare, Trash2, X, Plus, FileText, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const groupByDate = (conversations) => {
  const groups = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);

  conversations.forEach(conv => {
    const d = new Date(conv.updated_at || conv.created_at);
    let label;
    if (d >= today) label = "Aujourd'hui";
    else if (d >= yesterday) label = 'Hier';
    else if (d >= weekAgo) label = '7 derniers jours';
    else label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

    if (!groups[label]) groups[label] = [];
    groups[label].push(conv);
  });
  return groups;
};

const ChatHistorySidebar = ({
  isOpen,
  onClose,
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  onNewConversation,
  recentFiles,
}) => {
  const grouped = useMemo(() => groupByDate(conversations), [conversations]);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-30 lg:hidden" onClick={onClose} />
      )}

      <div
        data-testid="chat-history-sidebar"
        className={`fixed lg:relative z-40 top-0 left-0 h-full bg-white/95 backdrop-blur-xl border-r border-slate-200 flex flex-col transition-all duration-300 ease-in-out shadow-lg ${
          isOpen ? 'w-72 translate-x-0' : 'w-0 -translate-x-full lg:-translate-x-0 lg:w-0'
        } overflow-hidden`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
          <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Historique</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            data-testid="close-sidebar-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* New conversation */}
        <div className="p-3 flex-shrink-0">
          <Button
            onClick={onNewConversation}
            className="w-full justify-start gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-sm"
            data-testid="new-conversation-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvelle discussion
          </Button>
        </div>

        {/* Recent files (last 7 days) */}
        {recentFiles && recentFiles.length > 0 && (
          <div className="px-3 pb-3 flex-shrink-0">
            <div className="bg-cyan-50/80 rounded-lg p-3 border border-cyan-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-3.5 h-3.5 text-cyan-600" />
                <span className="text-xs font-medium text-cyan-700">Ajouts récents (7j)</span>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {recentFiles.slice(0, 8).map((file, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-slate-400 flex-shrink-0 ml-auto text-[10px]">
                      {new Date(file.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                ))}
                {recentFiles.length > 8 && (
                  <span className="text-[10px] text-slate-400">+{recentFiles.length - 8} autres</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Conversations list */}
        <ScrollArea className="flex-1">
          <div className="px-3 pb-3 space-y-4">
            {Object.entries(grouped).map(([label, convs]) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 px-2">{label}</p>
                <div className="space-y-0.5">
                  {convs.map(conv => (
                    <div
                      key={conv.id}
                      data-testid={`conversation-item-${conv.id}`}
                      className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                        activeConversationId === conv.id
                          ? 'bg-cyan-50 text-cyan-800 border border-cyan-200'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                      }`}
                      onClick={() => onSelectConversation(conv.id)}
                    >
                      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                      <span className="text-sm truncate flex-1">{conv.title}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onDeleteConversation(conv.id); }}
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 hover:bg-red-50 transition-opacity"
                        data-testid={`delete-conversation-${conv.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {conversations.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">Aucune discussion</p>
            )}
          </div>
        </ScrollArea>
      </div>
    </>
  );
};

export default ChatHistorySidebar;
